import {
  InteractionRequiredAuthError,
  BrowserAuthError,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { getAccessToken } from '@shared/auth/token.helper';

/** Internal marker so we only retry a request once. */
interface RetryableConfig extends InternalAxiosRequestConfig {
  _authRetry?: boolean;
}

export interface AuthInterceptorOptions {
  /**
   * Scopes to request when acquiring the bearer token. Passed
   * through to `getAccessToken`.
   */
  readonly scopes: readonly string[];
}

/**
 * Installs request + response interceptors on the given axios
 * instance:
 *   - Request: attach `Authorization: Bearer <token>` from MSAL.
 *   - Response: on HTTP 401, force a silent refresh and retry the
 *     original request exactly once.
 *
 * Returns an unregister function — useful for tests.
 */
export function installAuthInterceptor(
  axiosInstance: AxiosInstance,
  msalInstance: IPublicClientApplication,
  options: AuthInterceptorOptions,
): () => void {
  const scopes = [...options.scopes];

  const requestId = axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      const account = msalInstance.getActiveAccount();
      if (!account) {
        // No active account — let the request proceed; upstream
        // guards route unauthenticated users through login.
        return config;
      }
      try {
        const token = await getAccessToken(msalInstance, scopes);
        config.headers.set('Authorization', `Bearer ${token}`);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // `getAccessToken` has already navigated away via redirect.
          return config;
        }
        throw error;
      }
      return config;
    },
  );

  const responseId = axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetryableConfig | undefined;

      if (!original || error.response?.status !== 401 || original._authRetry) {
        return Promise.reject(error);
      }

      const account = msalInstance.getActiveAccount();
      if (!account) {
        return Promise.reject(error);
      }

      original._authRetry = true;

      try {
        const refreshed = await msalInstance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh: true,
        });
        const retryConfig: AxiosRequestConfig = {
          ...original,
          headers: {
            ...(original.headers as Record<string, string> | undefined),
            Authorization: `Bearer ${refreshed.accessToken}`,
          },
        };
        return await axiosInstance.request(retryConfig);
      } catch (refreshError) {
        if (refreshError instanceof InteractionRequiredAuthError) {
          try {
            await msalInstance.acquireTokenRedirect({ account, scopes });
          } catch (e) {
            // If another interaction is already in progress the redirect will
            // happen anyway — swallow the noise so no spurious toast fires.
            if (!(e instanceof BrowserAuthError && e.errorCode === 'interaction_in_progress')) {
              throw e;
            }
          }
        }
        return Promise.reject(refreshError);
      }
    },
  );

  return () => {
    axiosInstance.interceptors.request.eject(requestId);
    axiosInstance.interceptors.response.eject(responseId);
  };
}
